
import { AppSettings } from '@app-libs/model';

export class ConfigurationCore {
  // Centralized settings object
  protected settings: AppSettings = {
    general: {
      dangerouslyAllowBrowser: false,
      landingRoute: '/chat',
      logoUrl: 'assets/study-bru-mascot.png',
      logoUrlLight: 'assets/study-bru-mascot.png',
      appHeading: 'Study Bru', // Updated app heading
      // Your AI workforce. Tailored. Integrated. Ready
      headerSubheading: 'Your AI buddy for smashing school goals!', // Scale intelligence. Not headcount.
    },
    models: {
      openaiDefaultModel: 'gpt-4o',
      geminiDefaultModel: 'gemini-2.5-flash',
      buildTitleModel: 'gpt-4o-mini',
      triggerHeaderUpdateLengths: [2, 4, 6, 10, 15],
    },
    fileUploads: {
      allowedTypes: [
        'image/jpeg',
        'image/png',
        'image/gif',
        'image/webp',
        'application/pdf',
        'text/plain',
      ],
      audioCaptureFormat: 'm4a', // Default audio capture format
    },
    chat: {
      autoScrollConversation: false,
      autoScrollChat: true,
      defaultChatTitle: 'Workspace Guide',
    },
    userPreferences: {
      defaultPreferences: ['name', 'grade', 'age', 'program', 'school'],
      enableUserDetailsFunction: false,
    },
    stream: {
      chunkSeparator: '\n\n',
    },
  };

  getSetting<K extends keyof AppSettings, T extends keyof AppSettings[K]>(
    category: K,
    key: T
  ): AppSettings[K][T] {
    return this.settings[category][key];
  }

  // Update a setting dynamically
  updateSetting<K extends keyof AppSettings, T extends keyof AppSettings[K]>(
    category: K,
    key: T,
    value: AppSettings[K][T]
  ): void {
    this.settings[category][key] = value;
  }

  // Fetch settings from a database (placeholder for future implementation)
  async fetchSettingsFromDatabase(): Promise<void> {
    // Simulated fetch logic
    const dbSettings = await this.getSettingsFromDatabaseMock();
    if (dbSettings) {
      this.settings = { ...this.settings, ...dbSettings };
    }
  }

  // Placeholder for a mock database fetch
  private async getSettingsFromDatabaseMock(): Promise<any> {
    // Simulated delay
    await new Promise((resolve) => setTimeout(resolve, 500));
    // Mock database settings
    return {
      general: {
        appHeading: 'STUDY BRU - Updated from DB',
      },
      chat: {
        autoScrollChat: false,
      },
    };
  }

  // Accessors for specific settings
  get defaultModel(): string {
    return this.defaultOpenaiModel;
  }

  // Accessors for specific settings
  get defaultOpenaiModel(): string {
    return this.getSetting('models', 'openaiDefaultModel');
  }

  // Accessors for specific settings
  get defaultGeminiModel(): string {
    return this.getSetting('models', 'geminiDefaultModel');
  }

  get triggerHeaderUpdateLengths(): number[] {
    return this.getSetting('models', 'triggerHeaderUpdateLengths');
  }

  get allowedFileUploadTypes(): string[] {
    return this.getSetting('fileUploads', 'allowedTypes');
  }

  get landingRoute(): string {
    return this.getSetting('general', 'landingRoute');
  }

  get audioCaptureFormat(): string {
    return this.getSetting('fileUploads', 'audioCaptureFormat');
  }

  get default_chat_title(): string {
    return this.getSetting('chat', 'defaultChatTitle');
  }

  readonly FN_CAPTURE_PROFILE = `
## Function Calling and Data Capture:
  Capture any new personal or study-related information the student shares (e.g., goals, grade, age, learning preferences, favorite subjects, hobbies) as key-value pairs in 'user_attributes'. 

  **Important**:
  - Always respond to the user's query with a visible and meaningful message, even when calling a function.
  - Ensure 'user_attributes' contains meaningful key-value pairs and is never empty.
  - Avoid saving duplicate information; only capture new details that are not already known.
  `;

  readonly BUILD_CONVERSATION_TITLE = `
Generate a clear, descriptive title (max 80 characters) that summarizes the conversation. Display only the title as plain text—no quotes, formatting, or additional text.`;

  private readonly PREFERENCES_INSTRUCTION = `
## User Profile Preferences:
Use the following user-specific properties to tailor your communication, content, and approach dynamically.

### Dynamic User Properties:  
  {{REPLACE_PROPERTIES_HERE}}
---
`;

  private readonly ADDITIONAL_INFO_INSTRUCTIONS = `
## User Profile Instructions:
Use the following user-specific profile information to tailor your communication, content, and teaching approach dynamically.

### User Information:  
{{REPLACE_PROPERTIES_HERE}}
---
`;

  readonly CAPTURE_PROFILE_MESSAGE = `
**P.S.** Save your details like name, role, and job description in your **[profile](#navigate:/profile)** to personalize your StudyBru experience. Lekker learning awaits!
  `;

  getPreferencesInstruction(properties: string): string {
    return this.PREFERENCES_INSTRUCTION.replace(
      '{{REPLACE_PROPERTIES_HERE}}',
      properties
    );
  }

  getAdditionalInfoInstruction(properties: string): string {
    return this.ADDITIONAL_INFO_INSTRUCTIONS.replace(
      '{{REPLACE_PROPERTIES_HERE}}',
      properties
    );
  }
}
